import { useEffect, useState } from 'react';
import type { Fan, Take } from '@clutch/core';
import type { LibraryTake } from '../api';
import { useClient } from '../client';
import { AppLink, FanNav } from '../ui/Nav';
import { TakePlayer } from '../studio/Studio';

export function Library({ fan }: { fan: Fan }) {
  const client = useClient();
  const [items, setItems] = useState<LibraryTake[]>();
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [playing, setPlaying] = useState<Take>();
  useEffect(() => {
    let alive = true;
    setError(false);
    client.fanTakes(fan.fanId).then((takes) => alive && setItems(takes)).catch(() => alive && setError(true));
    return () => { alive = false; };
  }, [client, fan.fanId, retry]);
  return <main className="library" data-testid="library">
    <FanNav current="takes" />
    <header className="library__intro"><p className="eyebrow">YOUR MOMENTS, KEPT</p><h1>My takes<span>.</span></h1>
      <p>Every reaction you published. Play it back, send it to a friend, or jump back into the game.</p></header>
    {error ? <div className="empty-state" role="alert"><p>We couldn’t load your recordings.</p><button className="btn btn--ghost" onClick={() => setRetry((n) => n + 1)}>Try again</button></div>
      : !items ? <p role="status">Loading your takes…</p>
      : !items.length ? <div className="empty-state"><span className="empty-state__icon">◉</span><h2>Your next “I was there” lives here.</h2><p>Open a game and record a take. Preview it before you publish.</p><AppLink className="btn btn--primary" href="/">Find a game</AppLink></div>
      : <div className="take-grid">{items.map(({ take, game }) => <article className="saved-take" key={take.takeId} data-testid="saved-take">
        <button className="saved-take__preview" onClick={() => setPlaying(take)} aria-label={`Play take: ${game.away.tricode} at ${game.home.tricode}, ${new Date(take.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}>
          <video src={client.takeUrl(take)} muted playsInline preload="metadata" /><span className="saved-take__play" aria-hidden="true">▶</span>
          <span className="saved-take__duration">{Math.max(1, Math.round(take.durationMs / 1000))}s</span>
        </button>
        <div className="saved-take__body"><span className="eyebrow">{take.startedAtSeq < 0 ? 'PREGAME TAKE' : 'GAME REACTION'}</span>
          <h2>{game.away.tricode} <span>at</span> {game.home.tricode}</h2><p>{new Date(take.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {take.marks.length ? `${take.marks.length} plays captured` : 'Original recording'}</p>
          <AppLink href={`/room/${encodeURIComponent(take.roomId)}?take=${encodeURIComponent(take.takeId)}`}>Open in room ↗</AppLink>
        </div>
      </article>)}</div>}
    <p className="library__note">Your library follows this browser’s fan profile. Keep this browser’s site data to return as {fan.displayName}.</p>
    {playing && <TakePlayer take={playing} url={client.takeUrl(playing)} onClose={() => setPlaying(undefined)} onDeleted={() => setItems((items) => items?.filter(({ take }) => take.takeId !== playing.takeId))} />}
  </main>;
}
